import { message } from "antd";
import { type InjectedFormikProps } from "formik";
import { useCallback } from "react";

import { BTCheckbox } from "@btnet/core-components";

import { BdsButton } from "@buildertrend/components";

import { TempFileTypes } from "types/enum";

import { BTCKEditor } from "commonComponents/btWrappers/BTCKEditor/BTCKEditor";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTForm, BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTIconInfo, BTIconLegacyTrash } from "commonComponents/btWrappers/BTIcon";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import { BTUpload } from "commonComponents/btWrappers/BTUpload/BTUpload";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";
import { SignatureImage } from "commonComponents/utilities/SignatureImage/SignatureImage";

import { type IInternalUserHandler } from "entity/internalUser/InternalUserDetails/InternalUser.api.handler";
import {
    blankSignatureImage,
    type IInternalUserFormValues,
    type InternalUserEntity,
} from "entity/internalUser/InternalUserDetails/InternalUser.api.types";

interface IInternalUserMessagingProps {
    internalUserEntity: InternalUserEntity;
    isSelf: boolean;
    canEdit: boolean;
    handler?: IInternalUserHandler;
    inEditMode: boolean;
}

export const InternalUserMessaging: React.FC<
    InjectedFormikProps<IInternalUserMessagingProps, IInternalUserFormValues>
> = ({ values, setFieldValue, setFieldTouched, handleChange, canEdit, inEditMode }) => {
    const onUploadFile = useCallback(async () => {
        void message.info("Click Save to Upload File");
        return true;
    }, []);

    return (
        <PageSection title="Messaging">
            <BTForm>
                <div className="margin-bottom-md bds-col bds-gap-2">
                    <BTTitle>Message Preferences</BTTitle>
                    <div className="flex flex-row">
                        <div className="flex flex-col">
                            <BTFormItemAutomatic<IInternalUserFormValues> id="isOutOfOffice" useBds>
                                <BTCheckbox
                                    id="isOutOfOffice"
                                    data-testid="isOutOfOffice"
                                    onChange={handleChange}
                                    checked={values.isOutOfOffice}
                                    disabled={!canEdit || !inEditMode}
                                >
                                    Set Out of Office Status
                                </BTCheckbox>
                            </BTFormItemAutomatic>
                        </div>
                        <div className="flex flex-col padding-top-xs">
                            {canEdit && (
                                <BTPopover content="Turning on Out of Office does not prevent others from sending you messages.">
                                    <BTIconInfo />
                                </BTPopover>
                            )}
                        </div>
                    </div>
                    <BTFormItemAutomatic<IInternalUserFormValues> id="messageSignature" useBds>
                        <BTCKEditor<IInternalUserFormValues>
                            id="messageSignature"
                            data-testid="messageSignature"
                            onChange={setFieldValue}
                            onBlur={setFieldTouched}
                            value={values.messageSignature}
                            maxLength={20000}
                            readOnly={!inEditMode}
                        />
                    </BTFormItemAutomatic>
                    <BTFormItemAutomatic<IInternalUserFormValues> id="signatureImage" useBds>
                        <SignatureImage
                            url={values.tempFile ? values.tempFile.url : blankSignatureImage}
                        />
                    </BTFormItemAutomatic>
                    <BTRow>
                        <BTCol>
                            <BTFormItemAutomatic<IInternalUserFormValues> id="tempFile">
                                <BTUpload
                                    id="tempFile"
                                    data-testid="tempFile"
                                    setFieldValue={setFieldValue}
                                    value={values.tempFile}
                                    allowedFileTypes={["jpg", "jpeg", "gif", "png"]}
                                    tempFileType={TempFileTypes.Contacts}
                                    showUploadList={values.tempFile?.fileName !== ""}
                                    beforeUpload={onUploadFile}
                                    disabled={!inEditMode}
                                />
                            </BTFormItemAutomatic>
                        </BTCol>
                        <BTCol>
                            {values.tempFile && inEditMode && (
                                <BdsButton
                                    testid="remove-signature-image"
                                    className="margin-left-sm"
                                    onClick={() => {
                                        setFieldValue("signatureImageTempId", -1);

                                        setFieldValue("signatureImage", blankSignatureImage);
                                        setFieldValue("tempFile", undefined);
                                        void message.info("Click Save to Permanently Delete File");
                                    }}
                                    displayType="tertiary"
                                    icon={<BTIconLegacyTrash />}
                                    ariaLabel="Trash"
                                />
                            )}
                        </BTCol>
                    </BTRow>
                </div>
            </BTForm>
        </PageSection>
    );
};
